import { nextTick } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import NotFoundView from '@/views/404.vue'
import { getCookie } from '@/utils/token'
import { seoConfig } from '@/config/seo'
import { memcoreRoutes } from '@/views/memcore/routes'
import { setTrack } from '@/api/login'
import { installPageTracking } from '@/utils/pageTracking'

const routes = [
  {
    path: '/ai-open-platform', // 根路径（部署后的基础路径）
    redirect: '/ai-open-platform/home', // 期望重定向的目标路径
  },
  {
    path: '',
    redirect: '/home',
  },
  {
    path: '/',
    redirect: '/home',
  },
  {
    path: '/home',
    name: 'home',
    component: () => import('@/views/home/index.vue'),
    meta: seoConfig['/home'],
  },
  {
    path: '/omni-train',
    name: 'omniTrain',
    component: () => import('@/views/omni-train/index.vue'),
    meta: seoConfig['/omni-train'],
  },
  {
    path: '/datahub',
    name: 'datahub',
    component: () => import('@/views/datahub/index.vue'),
    meta: seoConfig['/datahub'],
  },
  {
    path: '/memcore',
    name: 'memcore',
    component: () => import('@/views/memcore/index.vue'),
    meta: seoConfig['/memcore/intro'],
    children: memcoreRoutes,
  },
  {
    path: '/memcore/intro',
    name: 'memcoreIntro',
    component: () => import('@/views/memcore/landing.vue'),
    meta: seoConfig['/memcore/intro'],
  },
  {
    path: '/ai-playground',
    name: 'aiPlayground',
    component: () => import('@/views/ai-playground/index.vue'),
    meta: seoConfig['/ai-playground'],
  },
  {
    path: '/uniclaw',
    name: 'uniclaw',
    component: () => import('@/views/uniclaw/index.vue'),
    meta: seoConfig['/uniclaw'],
  },
  {
    path: '/uniclaw/flash',
    name: 'uniclawFlash',
    component: () => import('@/views/uniclaw/flash.vue'),
    meta: seoConfig['/uniclaw/flash'],
  },
  {
    path: '/uniclaw/auth',
    name: 'uniclawAuth',
    component: () => import('@/views/uniclaw/auth.vue'),
    meta: seoConfig['/uniclaw/auth'],
  },
  {
    path: '/uniclaw/docs',
    name: 'uniclawDocs',
    component: () => import('@/views/uniclaw/docs.vue'),
    meta: seoConfig['/uniclaw/docs'],
  },
  // 核心能力
  {
    path: '/core_capabilities', // 根路径（部署后的基础路径）
    redirect: '/core_capabilities/edge_ai', // 期望重定向的目标路径
  },
  {
    path: '/core_capabilities/edge_ai',
    name: 'edge_ai',
    component: () => import('@/views/core/edge-ai.vue'),
    meta: seoConfig['/core_capabilities/edge_ai'],
  },
  {
    path: '/core_capabilities/edge_cloud_ai',
    name: 'cloud_ai',
    component: () => import('@/views/core/cloud-ai.vue'),
  },
  {
    path: '/core_capabilities/ai_audio',
    name: 'audio_ai',
    component: () => import('@/views/core/audio-ai.vue'),
    meta: seoConfig['/core_capabilities/ai_audio'],
  },
  {
    path: '/core_capabilities/ai_vision',
    name: 'vision_ai',
    component: () => import('@/views/core/vision-ai.vue'),
    meta: seoConfig['/core_capabilities/ai_vision'],
  },
  // 算法详情 —— 有 SEO 配置的保留为独立路由（静态路径优先于动态参数匹配）
  {
    path: '/algostore/face_landmarks_detection',
    name: 'Landmarks',
    component: () => import('@/views/algorithm-detail/index.vue'),
    meta: seoConfig['/algostore/face_landmarks_detection'],
  },
  {
    path: '/algostore/face_emotion',
    name: 'faceEmotion',
    component: () => import('@/views/algorithm-detail/index.vue'),
    meta: seoConfig['/algostore/face_emotion'],
  },
  {
    path: '/algostore/body_driver_monitoring',
    name: 'bodyDriverMonitoring',
    component: () => import('@/views/algorithm-detail/index.vue'),
    meta: seoConfig['/algostore/body_driver_monitoring'],
  },
  {
    path: '/algostore/body_gesture_recognition',
    name: 'bodyGestureRecognition',
    component: () => import('@/views/algorithm-detail/index.vue'),
    meta: seoConfig['/algostore/body_gesture_recognition'],
  },
  // 算法详情 —— 动态路由，根据后端 introduce.template 字段决定渲染哪个组件
  {
    path: '/algostore/:algorithmType',
    name: 'algorithmDetail',
    component: () => import('@/views/algorithm-detail/index.vue'),
  },
  //行业方案
  {
    path: '/massage_robot',
    name: 'massageRobot',
    component: () => import('@/views/industry_solution/index.vue'),
  },
  {
    path: '/solutions/smart_vending_machine',
    name: 'smartRetail',
    component: () => import('@/views/industry_solution/smart-vending.vue'),
    meta: seoConfig['/solutions/smart_vending_machine'],
  },
  {
    path: '/intelligent_sorting',
    name: 'intelligentSorting',
    component: () => import('@/views/industry_solution/index.vue'),
  },
  {
    path: '/solutions/audio_modules',
    name: 'pectorophony',
    component: () => import('@/views/industry_solution/audio-modules.vue'),
    meta: seoConfig['/solutions/audio_modules'],
  },
  {
    path: '/solutions/robrain',
    name: 'Robrain',
    component: () => import('@/views/industry_solution/robrain.vue'),
    meta: seoConfig['/solutions/robrain'],
  },
  {
    path: '/solutions/smart_scale',
    name: 'openVend',
    component: () => import('@/views/industry_solution/smart-scale.vue'),
    meta: seoConfig['/solutions/smart_scale'],
  },
  {
    path: '/voice_assistant',
    name: 'voiceAssistant',
    component: () => import('@/views/industry_solution/index.vue'),
  },
  {
    path: '/solutions/service_robot',
    name: 'businessRobot',
    component: () => import('@/views/industry_solution/service-robot.vue'),
  },
  {
    path: '/robot-v2',
    name: 'robotV2',
    component: () => import('@/views/industry_solution/index.vue'),
  },
  {
    path: '/solutions/robotherapist',
    name: 'robotherapist',
    component: () => import('@/views/industry_solution/robotherapist.vue'),
  },
  {
    path: '/solutions/smart_toys',
    name: 'aiToys',
    component: () => import('@/views/industry_solution/smart-toys.vue'),
    meta: seoConfig['/solutions/smart_toys'],
  },
  {
    path: '/algostore',
    name: 'aimarket',
    component: () => import('@/views/market/index.vue'),
    meta: seoConfig['/algostore'],
  },
  // {
  //   path: '/contact_us',
  //   name: 'contactUs',
  //   component: ContactUs,
  // },
  {
    path: '/aide',
    name: 'aide',
    component: () => import('@/views/tool-chain/aide.vue'),
  },
  {
    path: '/aide/guide',
    name: 'aideGuide',
    component: () => import('@/views/tool-chain/guide.vue'),
  },
  {
    path: '/aide/control-panel',
    name: 'controlPanel',
    component: () => import('@/views/tool-chain/index.vue'),
  },
  {
    path: '/aide/model-manager',
    name: 'modelManager',
    component: () => import('@/views/tool-chain/index.vue'),
  },
  {
    path: '/aide/model_convert',
    name: 'modelTransfer',
    component: () => import('@/views/tool-chain/model-forge.vue'),
  },
  {
    path: '/aide/performance',
    name: 'performance',
    component: () => import('@/views/tool-chain/index.vue'),
  },
  {
    path: '/aide/model-inference',
    name: 'modelInference',
    component: () => import('@/views/tool-chain/index.vue'),
  },
  {
    path: '/tool',
    name: 'tool',
    component: () => import('@/views/tool-chain/index.vue'),
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/redirect-login.vue'),
  },
  {
    path: '/auth/start',
    name: 'oauthStart',
    component: () => import('@/views/oauth-start.vue'),
  },
  {
    path: '/auth/callback',
    name: 'oauthCallback',
    component: () => import('@/views/oauth-callback.vue'),
  },
  {
    path: '/:pathMatch(.*)*', // 捕获所有未匹配到的路由
    name: 'not-found',
    component: NotFoundView,
  },
]

const router = createRouter({
  history: createWebHistory('/ai-open-platform'),
  routes,
  scrollBehavior(to, from, savedPosition) {
    // 1. 同路径（仅参数变化）：保留当前滚动位置
    if (to.path === from.path) {
      return { x: 0, y: window.scrollY }
    }
    // 2. 不同路径：强制回到顶部（Vue Router 内置逻辑）
    return { top: 0, left: 0, behavior: 'auto' }
  },
})

installPageTracking(router, setTrack)

router.afterEach((to, from) => {
  // 全局禁用滚动缓存
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual'
  }
  // 仅不同路径时，才执行滚动重置
  if (to.path !== from.path) {
    nextTick(() => {
      // 1. 强制关闭平滑滚动，确保滚动指令立即生效（火狐专属兼容优化）
      const scrollOptions = { top: 0, left: 0, behavior: 'auto' }

      // 2. 多滚动容器全覆盖，解决火狐滚动容器差异问题
      window.scrollTo(scrollOptions)
      document.documentElement.scrollTop = 0
      document.documentElement.scrollLeft = 0
      document.body.scrollTop = 0
      document.body.scrollLeft = 0

      const customScrollEl = document.querySelector('body')
      if (customScrollEl) {
        customScrollEl.scrollTop = 0
        customScrollEl.scrollLeft = 0
      }

      // 4. 兜底逻辑：防止极端场景下滚动失效
      setTimeout(() => {
        window.scrollTo(0, 0)
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
      }, 0)
    })
  }
})

// router.beforeEach((to, from, next) => {
//   // 仅在 fat.quectel.com.cn 域名下进行 Cookie 检测和登录重定向
//   const currentHost = window.location.hostname
//   const shouldCheckAuth = currentHost === 'fat.quectel.com.cn'

//   if (shouldCheckAuth) {
//     const hasToken = getCookie('bid_1_password_protected_auth')
//     if (!hasToken) {
//       const path = location.href
//       window.location.href = `https://fat.quectel.com.cn/?password-protected=login&redirect_to=${encodeURIComponent(
//         path,
//       )}`
//       next(false)
//     } else {
//       next()
//     }
//   } else {
//     // 其他环境正常通过
//     next()
//   }
// })

router.beforeEach((to, from, next) => {
  window.scrollTo(0, 0) // 滚动到顶部

  // 动态更新 TDK（客户端路由切换时，无配置则清空）
  document.title = to.meta?.title || ''

  let keywordsMeta = document.querySelector('meta[name="keywords"]')
  if (keywordsMeta) {
    keywordsMeta.content = to.meta?.keywords || ''
  }

  let descriptionMeta = document.querySelector('meta[name="description"]')
  if (descriptionMeta) {
    descriptionMeta.content = to.meta?.description || ''
  }

  next()
})

export default router
